import React from 'react';
import { Link } from 'react-router-dom';
import WhiteLogo from '@/assets/icons/white_logo_lg.png';

function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background" style={{ fontFamily: '"Inter", "SF Pro Display", system-ui, sans-serif' }}>
      <header className="border-b border-border px-6 py-5">
        <div className="mx-auto flex max-w-3xl items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 transition-opacity hover:opacity-80">
            <img src={WhiteLogo} alt="Staty" className="h-7 w-auto" />
          </Link>
          <Link
            to="/sign-up"
            className="text-sm font-medium text-muted-foreground underline-offset-2 hover:underline"
          >
            ← Back to sign up
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-12">
        <h1 className="mb-2 text-3xl font-semibold tracking-tight text-foreground">Privacy Policy</h1>
        <p className="mb-10 text-sm text-muted-foreground">Effective date: March 20, 2026</p>

        <div className="space-y-8 text-foreground">
          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">1. Information We Collect</h2>
            <p className="mb-3">We collect information you provide directly to us, as well as data generated through your use of the Service:</p>
            <ul className="list-disc space-y-1.5 pl-5">
              <li><strong className="font-medium text-foreground">Account information:</strong> Name, email address, and password (managed via Clerk)</li>
              <li><strong className="font-medium text-foreground">Organization data:</strong> League names, tournament details, division configurations, team rosters, and player information you enter</li>
              <li><strong className="font-medium text-foreground">Game data:</strong> Schedules, scores, statistics, and results</li>
              <li><strong className="font-medium text-foreground">Payment information:</strong> Billing details processed and stored by Stripe. We do not store full card numbers</li>
              <li><strong className="font-medium text-foreground">Usage data:</strong> Log data, IP addresses, browser type, and pages visited, collected automatically</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">2. How We Use Information</h2>
            <p className="mb-3">We use the information we collect to:</p>
            <ul className="list-disc space-y-1.5 pl-5">
              <li>Provide, operate, and maintain the Service</li>
              <li>Process payments and manage subscriptions</li>
              <li>Send transactional emails (account verification, password reset, receipts)</li>
              <li>Monitor and improve platform performance and reliability</li>
              <li>Detect and prevent fraud, abuse, and security incidents</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">3. Third-Party Services</h2>
            <p className="mb-3">We rely on trusted third-party providers to deliver the Service:</p>
            <ul className="list-disc space-y-1.5 pl-5">
              <li>
                <strong className="font-medium text-foreground">Clerk</strong>: Handles user authentication, session management, and identity verification. Clerk stores your email and authentication credentials securely.
              </li>
              <li>
                <strong className="font-medium text-foreground">Stripe</strong>: Processes payments and manages billing. Payment card data is handled directly by Stripe and subject to their privacy policy.
              </li>
              <li>
                <strong className="font-medium text-foreground">Sentry</strong>: Provides error monitoring and crash reporting to help us diagnose and fix issues. Error reports may include anonymized usage context.
              </li>
              <li>
                <strong className="font-medium text-foreground">MongoDB Atlas</strong>: Stores platform data securely in the cloud with encryption at rest.
              </li>
            </ul>
            <p className="mt-3">
              Each of these providers operates under their own privacy policies and data processing agreements.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">4. Data Sharing</h2>
            <p>
              We do not sell, rent, or trade your personal information to third parties. We may share information
              with service providers as described above, or when required by law (e.g., in response to a valid
              legal request). We may also share aggregated, anonymized data that cannot identify any individual.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">5. Data Retention</h2>
            <p>
              We retain your account and organization data for as long as your account is active or as needed
              to provide the Service. If you cancel your account, we may retain certain data for a reasonable
              period to comply with legal obligations, resolve disputes, or enforce our agreements. You may
              request deletion of your data by contacting us.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">6. Security</h2>
            <p>
              We implement industry-standard security measures including encryption in transit (TLS) and at rest,
              access controls, and regular security monitoring. However, no method of transmission over the
              internet is 100% secure. We encourage you to use a strong, unique password and to notify us
              immediately if you suspect unauthorized access to your account.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">7. Your Rights</h2>
            <p className="mb-3">Depending on your location, you may have the right to:</p>
            <ul className="list-disc space-y-1.5 pl-5">
              <li><strong className="font-medium text-foreground">Access</strong> the personal data we hold about you</li>
              <li><strong className="font-medium text-foreground">Correct</strong> inaccurate or incomplete data</li>
              <li><strong className="font-medium text-foreground">Delete</strong> your account and associated personal data</li>
              <li><strong className="font-medium text-foreground">Export</strong> your data in a portable format</li>
              <li><strong className="font-medium text-foreground">Object</strong> to or restrict certain processing</li>
            </ul>
            <p className="mt-3">
              To exercise these rights, please contact us at{' '}
              <a href="mailto:support@staty.app" className="font-medium text-foreground underline-offset-2 hover:underline">
                support@staty.app
              </a>.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">8. Children's Privacy</h2>
            <p>
              The Service is not directed to children under 13. We do not knowingly collect personal information
              from children under 13. If you believe a child under 13 has provided us with personal information,
              please contact us and we will take steps to delete such information promptly.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">9. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of significant changes by
              updating the effective date at the top of this page. Continued use of the Service after changes
              constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">10. Contact Information</h2>
            <p>
              If you have questions or concerns about this Privacy Policy or how we handle your data, please
              contact us at{' '}
              <a href="mailto:support@staty.app" className="font-medium text-foreground underline-offset-2 hover:underline">
                support@staty.app
              </a>.
            </p>
          </section>
        </div>
      </main>

      <footer className="border-t border-border px-6 py-8">
        <div className="mx-auto max-w-3xl text-center text-sm text-muted-foreground/60">
          <p>© {new Date().getFullYear()} Staty. All rights reserved.</p>
          <div className="mt-2 flex items-center justify-center gap-4">
            <Link to="/terms" className="hover:text-muted-foreground">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-muted-foreground">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default PrivacyPage;
